import { useState, useMemo, useEffect } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Control,
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
  FieldValues,
} from 'react-hook-form';
import { InterviewQuestion, InterviewQuestions, ProjectProps, FileData } from '@/types';
import { getGroupContentType, isQuestion, isGroup } from '@/utils/questionGroupHelpers';
import { GroupContentType, QuestionGroupLimits } from '@/enums';
import { GroupHeader } from './GroupHeader';
import EmptyGroupState from './EmptyGroupState';
import { QuestionBox } from '../projects/single-project/research-design/QuestionBox';
import { cn } from '@/lib/utils';

interface GroupContainerProps {
  // Group data
  groupData: {
    group: InterviewQuestions;
    groupIndex: number;
    groupPath?: number[];
    isSubgroup?: boolean;
    hasChildrenError?: boolean;
    childrenErrorMessage?: string;
  };
  // State flags
  state: {
    isDisabled?: boolean;
    isActiveDrag?: boolean;
    isAddingQuestionsDisabled?: boolean;
  };
  // Handlers
  handlers: {
    onGroupNameChange: (groupIndex: number, name: string, groupPath?: number[]) => void;
    onGroupRandomizeChange: (groupIndex: number, randomize: boolean, groupPath?: number[]) => void;
    onGroupDelete: (groupIndex: number, groupPath?: number[]) => void;
    onAddQuestion: (groupIndex: number, groupPath?: number[]) => void;
    onAddSubgroup?: (groupIndex: number, groupPath?: number[]) => void;
    onDeleteQuestion: (questionId: string) => void;
    onReorderQuestions?: (
      groupIndex: number,
      reorderedQuestions: InterviewQuestion[],
      groupPath?: number[],
    ) => void;
    onReorderSubgroups?: (
      groupIndex: number,
      reorderedSubgroups: InterviewQuestions[],
      groupPath?: number[],
    ) => void;
    onMoveQuestionBetweenGroups?: (
      questionId: string,
      fromPath: number[],
      toPath: number[],
      isDroppedOntoDroppableZone?: boolean,
    ) => void;
    isQuestionInDatabase: (questionId: string) => boolean;
  };
  // Form props
  formProps: {
    register: UseFormRegister<FieldValues>;
    control: Control<FieldValues>;
    setValue: UseFormSetValue<FieldValues>;
    clearErrors: UseFormClearErrors<FieldValues>;
    errors: FieldErrors<FieldValues>;
    // Bumped on every failed submit attempt — see the force-open effect below.
    submitAttempt: number;
  };
  // Question props
  questionProps: {
    questions: InterviewQuestion[];
    openQuestionId: string | null;
    setOpenQuestionId: React.Dispatch<React.SetStateAction<string | null>>;
    setFileData: React.Dispatch<React.SetStateAction<{ [key: string]: FileData }>>;
    fileData: { [key: string]: FileData };
    currentQuestions?: InterviewQuestion[];
  };
  // Project props
  projectProps: {
    isProjectPublished: boolean;
    isProjectCompleted: boolean;
    singleProjectData?: ProjectProps;
  };
}

export function GroupContainer({
  groupData,
  state,
  handlers,
  formProps,
  questionProps,
  projectProps,
}: GroupContainerProps) {
  // Destructure grouped props
  const {
    group,
    groupIndex,
    groupPath = [],
    isSubgroup = false,
    hasChildrenError = false,
    childrenErrorMessage,
  } = groupData;
  const { isDisabled = false, isActiveDrag = false, isAddingQuestionsDisabled = false } = state;
  const {
    onGroupNameChange,
    onGroupRandomizeChange,
    onGroupDelete,
    onAddQuestion,
    onAddSubgroup,
    onDeleteQuestion,
    onReorderQuestions,
    onReorderSubgroups,
    onMoveQuestionBetweenGroups,
    isQuestionInDatabase,
  } = handlers;
  const { register, control, setValue, clearErrors, errors, submitAttempt } = formProps;
  const { questions, openQuestionId, setOpenQuestionId, setFileData, fileData, currentQuestions } =
    questionProps;
  const { isProjectPublished, isProjectCompleted, singleProjectData } = projectProps;
  const [isOpen, setIsOpen] = useState(true);
  const contentType = getGroupContentType(group);

  const currentQuestionsMap = useMemo(() => {
    if (!currentQuestions) return new Map();
    return new Map(currentQuestions.map((q) => [q.id, q]));
  }, [currentQuestions]);

  const groupQuestions = useMemo(() => {
    return group.children.filter(isQuestion);
  }, [group.children, group.id, group.name]);

  // Build the full path to this group
  const currentPath = [...groupPath, groupIndex];

  // Helper to extract error for a subgroup at a specific index within this group
  const getSubgroupError = (subgroupIndex: number) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let errorObj: any = errors?.interview_questions;

    // eslint-disable-next-line no-restricted-syntax
    for (const pathIndex of currentPath) {
      if (!errorObj?.children?.[pathIndex]) {
        return { hasChildrenError: false, childrenErrorMessage: undefined };
      }
      errorObj = errorObj.children[pathIndex];
    }

    const subgroupError = errorObj?.children?.[subgroupIndex];
    if (!subgroupError || typeof subgroupError !== 'object') {
      return { hasChildrenError: false, childrenErrorMessage: undefined };
    }

    const hasError = 'children' in subgroupError && !!subgroupError.children;
    const errorMessage =
      hasError && subgroupError.children?.message
        ? (subgroupError.children.message as string)
        : undefined;

    return { hasChildrenError: hasError, childrenErrorMessage: errorMessage };
  };

  // The RHF error node rooted at THIS group (i.e. errors.interview_questions
  // walked down to `currentPath`). May itself be a "children not empty"
  // error, or contain a question error arbitrarily deep in its subtree.
  const getOwnErrorNode = (): unknown => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let errorObj: any = errors?.interview_questions;
    // eslint-disable-next-line no-restricted-syntax
    for (const pathIndex of currentPath) {
      if (!errorObj?.children?.[pathIndex]) return undefined;
      errorObj = errorObj.children[pathIndex];
    }
    return errorObj;
  };

  // Recursively checks whether ANY validation error exists anywhere under a
  // given RHF error node — a nested question error two levels down, this
  // group's own "not empty" error, etc.
  const hasErrorInSubtree = (node: unknown): boolean => {
    if (!node || typeof node !== 'object') return false;
    const record = node as Record<string, unknown>;
    if (typeof record.message === 'string' && record.message) return true;
    return Object.entries(record).some(([key, value]) => key !== 'ref' && hasErrorInSubtree(value));
  };

  // When a group is collapsed, its body — including any nested question or
  // subgroup — is unmounted below (`{isOpen && (...)}`), so a validation
  // error inside it has no DOM node to scroll to or highlight. Force the
  // group open whenever a submit attempt just failed and this group's
  // subtree contains an error. Keyed on `submitAttempt` (not `errors`
  // directly) so a collapsed-but-currently-valid group isn't forced open on
  // every keystroke — only right after a failed submit. TELV1-490.
  useEffect(() => {
    if (submitAttempt > 0 && hasErrorInSubtree(getOwnErrorNode())) {
      setIsOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitAttempt]);

  // Use group ID as the unique identifier for drag-and-drop operations
  const groupId = group.id || group.name; // Fallback to name for backward compatibility
  const droppableId = `group-droppable-${groupId}`;

  // All groups (top-level and subgroups) should be draggable for reordering
  // Only disable if the user doesn't have permission
  const shouldBeGroupDraggable = !isDisabled;

  // Use sortable for group reordering (both top-level and subgroups)
  const sortable = useSortable({
    id: groupId, // Use unique ID instead of name
    disabled: !shouldBeGroupDraggable,
  });

  // Use droppable for accepting items dropped into this group
  const droppable = useDroppable({
    id: droppableId,
    disabled: isDisabled,
  });

  const style = shouldBeGroupDraggable
    ? {
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
      }
    : undefined;

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  // Handler for adding questions
  // Rules:
  // - If empty: add question directly
  // - If contains questions: add another question
  // - If contains groups: create new subgroup with question inside
  const handleAddQuestionClick = () => {
    if (contentType === GroupContentType.EMPTY || contentType === GroupContentType.QUESTIONS) {
      // Add question directly to this group
      onAddQuestion(groupIndex, currentPath);
    } else if (contentType === GroupContentType.GROUPS && onAddSubgroup) {
      // Group contains subgroups - need to create a new subgroup with a question
      // This will be handled by creating a new subgroup
      onAddSubgroup(groupIndex, currentPath);
    }
  };

  // Handler for adding subgroups
  // Only available for top-level groups, not subgroups
  const handleAddSubgroupClick =
    !isSubgroup && onAddSubgroup ? () => onAddSubgroup(groupIndex, currentPath) : undefined;

  // Calculate if we're at the subgroup limit
  const existingSubgroupsCount = group.children.filter(isGroup).length;
  const isCreatingSubgroupDisabled =
    isDisabled ||
    existingSubgroupsCount >= QuestionGroupLimits.MAX_GROUPS ||
    isAddingQuestionsDisabled;

  // It should be disabled it if reached the subgroup limit
  const shouldDisableAddQuestionDueToSubgroupLimit =
    contentType === GroupContentType.GROUPS &&
    existingSubgroupsCount >= QuestionGroupLimits.MAX_GROUPS;

  // Border and background colors
  // Show green background when this specific group has randomization enabled
  const isGroupRandomized = group.randomize === true;

  let borderColor: string;
  if (hasChildrenError) {
    borderColor = 'border-red-500';
  } else if (isGroupRandomized) {
    borderColor = 'border-green-500';
  } else {
    borderColor = 'border-gray-300';
  }

  let bgColor: string;
  if (isGroupRandomized) {
    bgColor = 'bg-green-50';
  } else if (isSubgroup) {
    bgColor = 'bg-gray-50';
  } else {
    bgColor = 'bg-white';
  }

  // Combine refs for both sortable and droppable
  const setRefs = (node: HTMLDivElement | null) => {
    sortable.setNodeRef(node);
    droppable.setNodeRef(node);
  };

  return (
    <div
      ref={setRefs}
      style={style}
      className={cn(
        'flex flex-col w-full rounded-lg overflow-hidden border',
        sortable.isDragging ? 'border-dashed border-gray-400' : borderColor,
        bgColor,
        sortable.isDragging ? 'opacity-70' : 'shadow-sm',
        droppable.isOver && 'ring-2 ring-green-500',
        hasChildrenError && 'validation-error',
      )}
    >
      <GroupHeader
        group={group}
        isOpen={isOpen}
        isSubgroup={isSubgroup}
        isDisabled={isDisabled}
        isAddingQuestionsDisabled={
          isAddingQuestionsDisabled || shouldDisableAddQuestionDueToSubgroupLimit
        }
        isCreatingSubgroupDisabled={isCreatingSubgroupDisabled}
        hasChildrenError={hasChildrenError}
        childrenErrorMessage={childrenErrorMessage}
        onToggle={handleToggle}
        onNameChange={(name) => onGroupNameChange(groupIndex, name, currentPath)}
        onRandomizeChange={(randomize) =>
          onGroupRandomizeChange(groupIndex, randomize, currentPath)
        }
        onAddQuestionClick={handleAddQuestionClick}
        onAddSubgroupClick={handleAddSubgroupClick}
        onDeleteClick={() => onGroupDelete(groupIndex, currentPath)}
        dragHandleProps={shouldBeGroupDraggable ? sortable.attributes : undefined}
        dragListeners={shouldBeGroupDraggable ? sortable.listeners : undefined}
      />

      {isOpen && (
        <div className="p-4">
          {contentType === GroupContentType.EMPTY && (
            <SortableContext
              items={[]}
              strategy={verticalListSortingStrategy}
              disabled={isDisabled}
            >
              <EmptyGroupState
                onAddQuestions={() => onAddQuestion(groupIndex, currentPath)}
                onCreateSubgroup={() => onAddSubgroup?.(groupIndex, currentPath)}
                isDisabled={isDisabled}
                isAddingQuestionsDisabled={isAddingQuestionsDisabled}
                isSubgroup={isSubgroup}
                isCreatingSubgroupDisabled={isCreatingSubgroupDisabled}
              />
            </SortableContext>
          )}

          {contentType === GroupContentType.QUESTIONS && group.children && (
            <SortableContext
              items={group.children.filter(isQuestion).map((q) => q.id)}
              strategy={verticalListSortingStrategy}
              disabled={isDisabled}
            >
              <div className="flex flex-col gap-4">
                {groupQuestions.map((question, localIndex) => {
                  const currentQuestion = currentQuestionsMap.get(question.id);

                  // Build the full tree path for this question
                  // currentPath is the path to this group, append the question's local index
                  const questionTreePath = [...currentPath, localIndex];

                  // Build registration path: interview_questions.children[0].children[1]...
                  let registrationPath = 'interview_questions';
                  questionTreePath.forEach((idx) => {
                    registrationPath += `.children[${idx}]`;
                  });

                  // Extract error from the nested tree structure
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  let questionError: any = errors?.interview_questions;
                  // eslint-disable-next-line no-restricted-syntax
                  for (const idx of questionTreePath) {
                    if (questionError?.children?.[idx]) {
                      questionError = questionError.children[idx];
                    } else {
                      questionError = undefined;
                      break;
                    }
                  }

                  return (
                    <QuestionBox
                      key={question.id}
                      id={question.id}
                      treePath={questionTreePath}
                      registrationPath={registrationPath}
                      displayNumber={localIndex + 1}
                      register={register}
                      control={control}
                      handleDeleteQuestion={onDeleteQuestion}
                      isActiveDrag={isActiveDrag}
                      isUserViewer={isDisabled}
                      existingText={question.question}
                      isProjectPublished={isProjectPublished}
                      isProjectCompleted={isProjectCompleted}
                      questions={questions}
                      openQuestionId={openQuestionId}
                      setOpenQuestionId={setOpenQuestionId}
                      setFileData={setFileData}
                      fileData={fileData}
                      currentQuestion={currentQuestion}
                      setValue={setValue}
                      clearErrors={clearErrors}
                      error={questionError?.question}
                      singleProjectData={singleProjectData}
                      isQuestionSaved={isQuestionInDatabase(question.id)}
                    />
                  );
                })}
              </div>
            </SortableContext>
          )}

          {contentType === GroupContentType.GROUPS && group.children && (
            <SortableContext
              items={group.children.filter(isGroup).map((g) => g.id || g.name)}
              strategy={verticalListSortingStrategy}
              disabled={isDisabled}
            >
              <div className="flex flex-col gap-4">
                {group.children.filter(isGroup).map((subgroup, index) => {
                  const {
                    hasChildrenError: subgroupHasError,
                    childrenErrorMessage: subgroupErrorMessage,
                  } = getSubgroupError(index);
                  return (
                    <GroupContainer
                      key={subgroup.id || subgroup.name}
                      groupData={{
                        group: subgroup,
                        groupIndex: index,
                        groupPath: currentPath,
                        isSubgroup: true,
                        hasChildrenError: subgroupHasError,
                        childrenErrorMessage: subgroupErrorMessage,
                      }}
                      state={{
                        isDisabled,
                        isActiveDrag,
                        isAddingQuestionsDisabled,
                      }}
                      handlers={{
                        onGroupNameChange,
                        onGroupRandomizeChange,
                        onGroupDelete,
                        onAddQuestion,
                        onAddSubgroup,
                        onDeleteQuestion,
                        onReorderQuestions,
                        onReorderSubgroups,
                        onMoveQuestionBetweenGroups,
                        isQuestionInDatabase,
                      }}
                      formProps={{
                        register,
                        control,
                        setValue,
                        clearErrors,
                        errors,
                        submitAttempt,
                      }}
                      questionProps={{
                        questions,
                        openQuestionId,
                        setOpenQuestionId,
                        setFileData,
                        fileData,
                        currentQuestions,
                      }}
                      projectProps={{
                        isProjectPublished,
                        isProjectCompleted,
                        singleProjectData,
                      }}
                    />
                  );
                })}
              </div>
            </SortableContext>
          )}
        </div>
      )}
    </div>
  );
}
